(ns app.front.view.group.admin.fees.schedule.schedule-core
  (:require [app.domain.fee.model :as fee-model]
            [app.domain.group.config.model :as config-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.front.ui :as ui]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.money-input :as mi]
            [app.front.ui.section :as section]
            [app.front.ui.separator :as ui-sep]
            [app.front.util :as futil]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.fees.schedule.schedule-events :as fs-events]
            [app.front.view.group.admin.fees.schedule.schedule-subs :as fs-subs]
            [app.front.view.group.admin.fees.schedule.template-variable-editor :as template-variable-editor]
            [app.front.view.group.admin.fees.schedule.ui :refer [grid-3 grid-4 h2 p]]
            [app.front.view.group.admin.permit-type-select :as permit-type-select]
            [app.front.view.group.admin.subs :as gas]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


;;; Init


(rf/reg-event-fx
  ::init
  (fn [_ [_ authority-group-id]]
    {:dispatch-n [[::fs-events/initialize-db authority-group-id]
                  [::fs-events/fetch-permit-type-configs authority-group-id]]}))


;;; Components

(defn fee-schedule-header [{fee-schedule-id    :db/id
                            :fee-schedule/keys [title titles]
                            :as                fee-schedule}
                           tr
                           disabled?]
  (let [get-current-value  #(or (<sub [::fs-subs/unsaved-fee-schedule-field fee-schedule-id %])
                                (get fee-schedule %))
        current-valid-from (get-current-value :fee-schedule/valid-from)]
    [:div
     [:h2.mb-4.text-sm.font-bold.leading-none (tr [:fees/schedule-name-and-localization])]
     [grid-3
      [:div.col-span-2.flex.flex-row.gap-4.items-end
       [ui-i18n/edit-language-selector {:allowed-locales-sub ::gas/allowed-locales}]
       [ui-i18n/localized-string-edit
        {:id                  fee-schedule-id
         :type                :text
         :label-text          (tr [:fees/title])
         :light               true
         :required            true
         :strings             (or (<sub [::fs-subs/unsaved-fee-schedule-field fee-schedule-id :fee-schedule/titles])
                                  (i18n-model/strings-map titles title))
         :disabled            disabled?
         :on-change           #(rf/dispatch-sync [::fs-events/change-field fee-schedule-id :fee-schedule/titles %])
         :allowed-locales-sub ::gas/allowed-locales}]]
      [ui/date-picker {:label-text (tr [:fees/valid-from])
                       :min-date   (date-util/tomorrow-start-of-day)
                       :value      current-valid-from
                       :disabled?  disabled?
                       :callback   #(>evt [::fs-events/change-field fee-schedule-id :fee-schedule/valid-from %])}]]
     [ui-sep/horizontal {:class "mt-8"}]]))


(defn fee-schedule-combinations [fee-schedule tr]
  (let [fee-schedule-id      (:db/id fee-schedule)
        selected-permit-type (<sub [::fs-subs/selected-permit-type fee-schedule-id])
        selected-category    (<sub [::fs-subs/selected-category-id fee-schedule-id])
        selected-work-type   (<sub [::fs-subs/selected-work-type-id fee-schedule-id])
        selected-work-target (<sub [::fs-subs/selected-work-target-id fee-schedule-id])
        on-change            (fn [new-value]
                               (>evt [::fs-events/select-permit-type-tuple fee-schedule-id new-value]))]
    [:div.mb-4
     [:h2.mt-8.mb-2.text-sm.font-bold.leading-none (tr [:fees/configure-combinations])]
     [ui/help (tr [:fees/help-text]) {:color :blue}]
     [permit-type-select/permit-type-select
      (util/mmap {:selected-tuple             [selected-permit-type
                                               selected-category
                                               selected-work-type
                                               selected-work-target]
                  :all-categories-placeholder :fees/category-all}
                 tr on-change)]]))

(defn fee-schedule-product [fee-schedule tr disabled?]
  (let [fee-schedule-id      (:db/id fee-schedule)
        selected-permit-type (or (<sub [::fs-subs/selected-permit-type fee-schedule-id])
                                 (:fee-schedule/permit-type fee-schedule))
        on-change            (fn [new-value]
                               (>evt [::fs-events/change-field fee-schedule-id :fee-schedule/permit-type (first new-value)])
                               (>evt [::fs-events/select-permit-type-tuple fee-schedule-id new-value]))]
    [:div.mb-4
     [:h2.mt-8.mb-2.text-sm.font-bold.leading-none (tr [:application-type])]
     [permit-type-select/permit-type-select
      (util/mmap {:selected-tuple          [selected-permit-type]
                  :disabled                disabled?
                  :selectable-permit-types (->> (<sub [::gas/group-permit-types])
                                                (filter ptc-model/primary-permit-types))
                  :levels                  #{:product}}
                 tr on-change)]]))

(defn manage-fee-items-button [tr {:keys [published?] :as fee-schedule}]
  [:div.text-right
   (cc/button {:kind     :tertiary
               :on-click #(>evt [::fs-events/open-fee-template-editor fee-schedule published?])}
              (if published?
                (tr [:fees/view-fee-items])
                (tr [:fees/manage-fee-items])))])


(defn fee-structure-settings
  "Renders the minimum-total and fee template options for the selected target-path"
  [{:keys [fee-schedule tr disabled?]}]
  (let [fee-schedule-id      (:db/id fee-schedule)
        fee-template-states  (<sub [::fs-subs/template-states fee-schedule-id])
        lang                 (<sub [:language])
        selected-category    (<sub [::fs-subs/selected-category-config fee-schedule-id])
        selected-work-type   (<sub [::fs-subs/selected-work-type-config fee-schedule-id])
        selected-work-target (<sub [::fs-subs/selected-work-target-config fee-schedule-id])
        fee-structure-path   (->> [selected-category
                                   selected-work-type
                                   selected-work-target]
                                  (filter some?)
                                  (map #(config-model/localized-name % lang))
                                  (str/join " / ")
                                  (#(when-not (empty? %) (str "(" % ")"))))
        current-path         (<sub [::fs-subs/group-config-id-path fee-schedule-id])
        fee-templates        (<sub [::fs-subs/filtered-fee-templates fee-schedule-id])
        no-fee-templates?    (empty? fee-templates)
        lang                 (<sub [:language])]
    [:<>
     [:div.flex
      [h2 (tr [:fees/settings-for-selected-combination])]
      [:span.ml-2 fee-structure-path]]
     [p (tr [:fees/selected-combination-help])]
     ;; TODO: TT-22163 The minimums do not work, so remove the controls waiting for possible fix
     ;; [minimums/bill-minimums-editor (util/mmap disabled? fee-schedule-id)]
     [grid-4
      [:div.col-span-3.p-0 (when-not disabled?
                             (if no-fee-templates?
                               (tr [:fees/no-fee-items])
                               (tr [:fees/set-fee-items-for-combination])))]
      (when-not no-fee-templates?
        [:div.col-span-2
         [ui/search {:id                      (str "fees/item-filter/" fee-schedule-id)
                     :placeholder             (tr [:fees/find-fee-items-by-name])
                     :value                   (<sub [::fs-subs/fee-item-filter])
                     :clear-fn                #(>evt [::fs-events/fee-item-filter-changed ""])
                     :on-change               #(rf/dispatch-sync [::fs-events/fee-item-filter-changed (futil/tval %)])
                     :close-button-label-text (tr [:search/empty])}]])
      [:div.col-span-4
       (futil/for-frag [[phase templates] fee-templates]
         [acommon/form-group
          {:legend-text (tr [phase])}
          (for [{:keys [template
                        grayed-out?
                        highlighted?]} templates
                :let                   [fee-template-id (:db/id template)
                                        fee-template    (p/find-first fee-template-states #(= (:id %) fee-template-id))]]
            (cc/checkbox {:id         (str fee-template-id)
                          :key        (str "fee-template-" fee-template-id)
                          :checked    (-> fee-template :checked? boolean)
                          :disabled   (boolean (or (-> fee-template :disabled?)
                                                   disabled?))
                          :class-name (futil/cs "transition-colors duration-300 pr-2"
                                                (when grayed-out? "opacity-50")
                                                (when highlighted? "bg-yellow-300 rounded"))
                          :on-change  #(>evt [::fs-events/toggle-fee-template fee-schedule-id current-path fee-template-id %])
                          :label-text (or (fee-model/fee-title template lang tr) "")}))])]]]))

(defn fee-list
  [{:keys [fee-schedule tr]}]
  (let [fee-schedule-id (:db/id fee-schedule)
        fee-templates   (<sub [::fs-subs/filtered-fee-templates fee-schedule-id])
        lang            (<sub [:language])
        template-label  #(or (fee-model/fee-title (:template %) lang tr) "")]
    [:div.mb-4
     [:h2.mt-8.mb-2.text-sm.font-bold.leading-none (tr [:fees/list-of-available-fees])]
     [grid-4
      (futil/for-frag [[phase templates] (sort-by first fee-templates)
                       :let [templates (->> templates
                                            (map #(assoc % :label (template-label %)))
                                            (sort-by :label))]]
        [acommon/form-group
         {:legend-text (tr [phase])}
         (for [{:keys [label]} templates]
           [:div.mt-1.mb-2 label])])]]))

(defn reversal-method-settings
  "Renders reversal method assignment options for the selected target-path"
  [{:keys [fee-schedule tr disabled?] :as _props}]
  (let [fee-schedule-id        (:db/id fee-schedule)
        reversal-methods       (<sub [::fs-subs/filtered-reversal-methods fee-schedule-id])
        no-methods?            (empty? reversal-methods)
        reversal-method-states (<sub [::fs-subs/reversal-method-states fee-schedule-id])
        current-path           (<sub [::fs-subs/group-config-id-path fee-schedule-id])
        lang                   (<sub [:language])]
    [:<>
     [:div.mt-8
      [h2 (tr [:fees/reversal-methods])]
      [p (tr [:fees/select-reversal-methods-help])]]
     [grid-4
      [:div.col-span-3.p-0 (when-not disabled?
                             (if no-methods?
                               (tr [:fees/no-reversal-methods])
                               (tr [:fees/set-reversal-methods-for-schedule])))]
      (when-not no-methods?
        [:div.col-span-2
         [ui/search {:id                      (str "reversal-methods/filter/" fee-schedule-id)
                     :placeholder             (tr [:fees/find-reversal-methods-by-name])
                     :value                   (<sub [::fs-subs/reversal-method-filter])
                     :clear-fn                #(>evt [::fs-events/reversal-method-filter-changed ""])
                     :on-change               #(rf/dispatch-sync [::fs-events/reversal-method-filter-changed (futil/tval %)])
                     :close-button-label-text (tr [:search/empty])}]])
      [:div.col-span-4
       (futil/for-all [{:keys [method
                               grayed-out?
                               highlighted?]} reversal-methods]
         (let [method-id       (:db/id method)
               method-type     (:bill-reversal-method/type method)
               reversal-method (p/find-first reversal-method-states #(= (:id %) method-id))]
           ^{:key (str "reversal-method-block-" fee-schedule-id "-" method-id)}
           [:div.mb-4
            [:div.flex.items-center.mb-2
             (cc/checkbox {:id         (str "reversal-" fee-schedule-id "-" method-id)
                           :key        (str "reversal-checkbox-" fee-schedule-id "-" method-id)
                           :checked    (-> reversal-method :checked? boolean)
                           :disabled   (boolean (or (-> reversal-method :disabled?)
                                                    disabled?))
                           :class-name (futil/cs "transition-colors duration-300 pr-2"
                                                 (when grayed-out? "opacity-50")
                                                 (when highlighted? "bg-yellow-300 rounded"))
                           :on-change  #(>evt [::fs-events/toggle-reversal-method fee-schedule-id current-path method-id %])
                           :label-text (fee-model/reversal-method-title method lang)})]
            (when (and (-> reversal-method :checked? boolean) (= method-type :bill-reversal-method.type/nsf))
              [:div.ml-6.max-w-xs.space-y-4
               [mi/money-input
                {:id            (str "nsf-amount-" method-id)
                 :label-text    (tr [:fees/nsf-amount])
                 :default-value (-> reversal-method :nsf-amount)
                 :required      true
                 :disabled      (or (-> reversal-method :disabled?)
                                    disabled?)}
                #(>evt [::fs-events/update-reversal-method-nsf-amount
                        fee-schedule-id current-path method-id %])]
               [ui/text-input
                {:id         (str "nsf-gl-code-" method-id)
                 :label-text (tr [:fees/nsf-gl-code])
                 :value      (:nsf-gl-code reversal-method)
                 :disabled   (or (-> reversal-method :disabled?)
                                 disabled?)
                 :on-change  #(>evt [::fs-events/update-reversal-method-nsf-gl-code
                                     fee-schedule-id current-path method-id (futil/tval %)])}]])]))]]]))

(defn fee-schedule-view [{:keys [fee-schedule authority-group-id tr]}]
  (let [fee-schedule-id           (:db/id fee-schedule)
        lang                      (<sub [:language])
        title                     (or (fee-model/fee-schedule-title fee-schedule lang)
                                      (tr [:fees/untitled-fee-schedule]))
        current?                  (some-> (<sub [::fs-subs/current-fee-schedule])
                                          :db/id
                                          (= fee-schedule-id))
        draft-exists?             (<sub [::fs-subs/draft-schedule-exists?])
        published?                (:published? fee-schedule)
        archived?                 (:fee-schedule/archived? fee-schedule)
        options                   {:tr                   tr
                                   :initially-collapsed? (and published?
                                                              (or draft-exists?
                                                                  (not current?)))
                                   :on-expand            #(>evt [::fs-events/fetch-schedule authority-group-id fee-schedule-id])}
        no-unsaved-changes?       (<sub [::fs-subs/no-unsaved-changes? fee-schedule-id])
        copy-disabled?            (<sub [::fs-subs/copy-disabled?])
        archive-disabled?         (<sub [::fs-subs/archive-disabled?])
        superdomain-group?        (<sub [::gas/superdomain-group?])
        reversal-methods-enabled? (when-not superdomain-group?
                                    (<sub [::ff/enabled? :feature-flag.id/bill-reversal-methods]))]
    [section/section fee-schedule-id options title
     [eb/error-boundary [fee-schedule-header fee-schedule tr published?]]
     (if superdomain-group?
       [eb/error-boundary [fee-schedule-product fee-schedule tr published?]]
       [eb/error-boundary [fee-schedule-combinations fee-schedule tr]])
     [:hr.my-8.h-px.border-0.bg-gray-400]
     [eb/error-boundary
      (let [props (util/mmap {:disabled? published?} fee-schedule tr)]
        (if superdomain-group?
          [fee-list props]
          [fee-structure-settings props]))]
     [ui-sep/horizontal]
     (when reversal-methods-enabled?
       [eb/error-boundary
        (let [props (util/mmap {:disabled? published?} fee-schedule tr)]
          [reversal-method-settings props])])
     (when reversal-methods-enabled?
       [ui-sep/horizontal])
     [eb/error-boundary
      [:div.col-span-4.flex.justify-end.mt-8.gap-4
       [manage-fee-items-button tr fee-schedule]
       ;; Export
       (cc/button {:kind :ghost
                   :href (str "/api/command/fee/export-fee-schedule?fee-schedule-id=" fee-schedule-id)}
                  (tr [:export]))
       [:div.flex-1]
       ;; Archive
       (when (and published?
                  (not current?))
         (if archived?
           [ui/modal-confirmation-button
            {:tr                  tr
             :id                  "unarchive-fee-schedule-confirmation"
             :on-yes-click        #(>evt [::fs-events/unarchive-fee-schedule fee-schedule-id])
             :trigger-button-text (tr [:fees/unarchive])
             :trigger-button-opts {:kind     :tertiary
                                   :disabled archive-disabled?}
             :modal-title         (tr [:fees/unarchive-schedule])}
            [:div
             [:div (tr [:fees/unarchive-schedule-confirmation])]
             [:div.font-bold title]]]
           [ui/modal-confirmation-button
            {:tr                  tr
             :id                  "archive-fee-schedule-confirmation"
             :on-yes-click        #(>evt [::fs-events/archive-fee-schedule fee-schedule-id])
             :trigger-button-text (tr [:fees/archive])
             :trigger-button-opts {:kind     :tertiary
                                   :disabled archive-disabled?}
             :modal-title         (tr [:fees/archive-schedule])}
            [:div
             [:div (tr [:fees/archive-schedule-confirmation])]
             [:div.font-bold title]]]))
       ;; Duplicate
       (when published?
         (cc/button {:disabled copy-disabled?
                     :on-click #(>evt [::fs-events/copy-fee-schedule fee-schedule-id])}
                    (tr [:duplicate])))
       ;; Delete
       (when-not published?
         [ui/modal-confirmation-button
          {:tr                  tr
           :id                  "delete-fee-schedule"
           :on-yes-click        #(>evt [::fs-events/delete-fee-schedule fee-schedule-id])
           :trigger-button-text (tr [:fees/delete-schedule])
           :trigger-button-opts {:kind (if (or published? no-unsaved-changes?) :tertiary :ghost)}}
          (tr [:fees/confirm-delete-fee-schedule])])
       ;; Cancel
       (when-not (or published? no-unsaved-changes?)
         [ui/modal-confirmation-button
          {:tr                  tr
           :id                  "confirm-cancel-editing"
           :on-yes-click        #(>evt [::fs-events/cancel fee-schedule-id])
           :trigger-button-text (tr [:cancel])
           :trigger-button-opts {:kind :tertiary}}
          (tr [:fees/confirm-cancel-fee-schedule-editing])])
       ;; Save
       (when-not published?
         (if no-unsaved-changes?
           [ui/modal-confirmation-button
            {:tr                  tr
             :id                  "publish-fee-schedule"
             :disabled?           (fee-model/fee-schedule-has-no-title? fee-schedule)
             :on-yes-click        #(>evt [::fs-events/publish-fee-schedule fee-schedule-id])
             :trigger-button-text (tr [:fees/publish-now])
             :trigger-button-opts {:kind :primary}}
            (tr [:fees/confirm-publish-fee-schedule])]
           (cc/button {:on-click #(>evt [::fs-events/save fee-schedule-id superdomain-group?])
                       :disabled (or (<sub [::fs-subs/blank-fee-schedule-title? fee-schedule-id])
                                     (<sub [::fs-subs/reversal-nsf-missing? fee-schedule-id]))}
                      (tr [:save]))))]]]))

(defn view [fee-schedules & {:keys [empty-placeholder]
                             :or   {empty-placeholder :fees/no-schedules}}]
  (let [tr                        (<sub [::gas/group-tr])
        authority-group-id        (<sub [::gas/group-id])
        fee-template-editor-open? (<sub [::fs-subs/fee-template-editor-open?])
        fee-schedules-loading?    (<sub [::fs-subs/fee-schedules-loading?])]
    [:<>
     (when fee-template-editor-open?
       [:<>
        [:div.fixed.inset-0.z-10 {:style {:background "rgba(0,0,0,0.5)"}}]
        [template-variable-editor/view (util/mmap tr)]])
     [:div
      (when fee-template-editor-open? {:inert ""})
      (when fee-schedules-loading?
        (cc/loading-spinner))
      (if-not (empty? fee-schedules)
        (futil/for-frag [fee-schedule fee-schedules]
          [fee-schedule-view (util/mmap fee-schedule tr authority-group-id)])
        [:section
         [:div (tr [empty-placeholder])]])]]))
